import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import gtm from 'general/js/gtm';
import {
    getRoundById,
    generateBemCss,
    isRoundChoicesEqual,
    isRoundChoiceAvailable,
    setSelectedChoice,
} from 'general/js/helpers';
import { LiveEventSelect, scorecardTheme } from 'components/live-event-select';
import {
    getHoleData
} from './live-player-helpers';
import LivePlayerScores from './live-player-scores';
import { playerSitecoreData } from './enums';
import { PLAYOFF_ROUND_ID } from '../../../general/js/constants';
import { isPlayOff } from '../../shot-tracker/js/shot-tracker-helpers';


class LivePlayerScorecard extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            roundChoices: null,
            selectedRoundId: null,
            hasUserSelected: false,
        };
    }

    componentDidMount() {
        this.updateChoices();
    }

    componentDidUpdate() {
        this.updateChoices();
    }

    setRound = (roundId) => {
        const { firstName, lastName } = this.getSitecoreData();

        gtm.push('Player Card', `Round Scores > ${roundId}`, `${firstName} ${lastName}`);
        this.setState({ selectedRoundId: roundId, hasUserSelected: true });
    }

    getSitecoreData() {
        return get(this.props, 'scoringPlayer.sitecoreData') || playerSitecoreData;
    }

    getAvailableChoices() {
        const { liveScanner, scoringPlayer } = this.props;
        const roundChoices = get(liveScanner, 'liveComponents.playerScoreCard.roundChoices');

        if (!Array.isArray(scoringPlayer.rounds)) return [];

        return cloneDeep(roundChoices).filter((choice) => {
            return !!getRoundById(scoringPlayer.rounds, choice.value);
        });
    }

    updateChoices() {
        const { liveScanner, scoringPlayer } = this.props;
        const { selectedRoundId } = this.state;

        if (!liveScanner || !scoringPlayer.currentRound) return;

        const prevChoices = this.state.roundChoices;
        const roundChoices = this.getAvailableChoices();

        if (this.state.hasUserSelected &&
            isRoundChoiceAvailable(roundChoices, selectedRoundId)
        ) {
            setSelectedChoice(roundChoices, selectedRoundId);

            if (!isRoundChoicesEqual(prevChoices, roundChoices)) {
                this.setState({ roundChoices });
            }
            return;
        }

        const lastPlayedHole = getHoleData(scoringPlayer, roundChoices, null, true);
        const { round } = lastPlayedHole;

        if (isRoundChoiceAvailable(roundChoices, round)) {
            setSelectedChoice(roundChoices, round);

            this.setState({
                roundChoices,
                selectedRoundId: round,
                hasUserSelected: true,
            });
            return;
        }

        if (isRoundChoiceAvailable(isRoundChoiceAvailable, scoringPlayer.currentRound)) {
            setSelectedChoice(roundChoices, scoringPlayer.currentRound);

            this.setState({
                roundChoices,
                selectedRoundId: scoringPlayer.currentRound,
                hasUserSelected: true,
            });
            return;
        }

        const foundChoice = isPlayOff(roundChoices) ?
            roundChoices.find((choice) => choice.selected === true && choice.value === PLAYOFF_ROUND_ID.toString()) :
            roundChoices.find((choice) => choice.selected === true);

        if (!foundChoice && roundChoices.length) {
            const lastChoice = roundChoices[roundChoices.length - 1];

            lastChoice.selected = true;
            this.setState({
                roundChoices,
                selectedRoundId: lastChoice.value,
                hasUserSelected: true,
            });
            return;
        }

        if (isRoundChoicesEqual(prevChoices, roundChoices)) return;

        this.setState({
            roundChoices,
            selectedRoundId: (foundChoice && foundChoice.value) || null,
            hasUserSelected: false,
        });
    }

    render() {
        const {
            scoringCourse, scoringPlayer, isWithdrawn, setClipTagParameters, clipTagParameters, isClipTagLoaded
        } = this.props;
        const { roundChoices, selectedRoundId } = this.state;

        if (!roundChoices ||
            !roundChoices.length ||
            !scoringCourse ||
            !Array.isArray(scoringCourse.courseRounds) ||
            !Array.isArray(scoringPlayer.rounds)
        ) {
            return null;
        }

        const bemList = [];

        if (isWithdrawn) {
            bemList.push('withdrawn');
        }

        const bemCss = generateBemCss('live-player-scorecard', bemList);
        const scoringCourseRound = getRoundById(scoringCourse.courseRounds, selectedRoundId);
        const scoringPlayerRound = getRoundById(scoringPlayer.rounds, selectedRoundId);

        return (
            <div className={bemCss}>
                <div className="live-player-scorecard__dropdown">
                    {
                        roundChoices.length === 1 &&
                        <div className="single-round">{roundChoices[0].label}</div>
                    }
                    {
                        roundChoices.length > 1 &&
                        <LiveEventSelect
                            choices={roundChoices}
                            theme={scorecardTheme}
                            onChange={this.setRound}
                        />
                    }
                </div>
                <div className="live-player-scorecard__list">
                    {
                        scoringCourseRound &&
                        Array.isArray(scoringCourseRound.holePars) &&
                        <LivePlayerScores
                            scoringPlayerRound={scoringPlayerRound}
                            scoringCourseRound={scoringCourseRound}
                            isClipTagLoaded={isClipTagLoaded}
                            clipTagParameters={clipTagParameters}
                            isWithdrawn={isWithdrawn}
                            setClipTagParameters={setClipTagParameters}
                            selectedRoundId={parseInt(selectedRoundId)}
                            isFull
                            isScrollable
                        />
                    }
                </div>
            </div>
        );
    }
}

LivePlayerScorecard.propTypes = {
    choices: PropTypes.array,
    scoringCourse: PropTypes.object,
    setScoreRound: PropTypes.func,
    isClipTagLoaded: PropTypes.bool,
    setClipTagParameters: PropTypes.func,
    clipTagParameters: PropTypes.object
};

export default LivePlayerScorecard;
